import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  getContactChannel,
  isContactShowing,
  normalizeLoginSupportStyle,
  telegramHandleLabel,
} from "../common/loginPage";
import CommonPopup from "./CommonPopup";
import PrivacyPopup from "./Profile/PrivacyPopup";
import TermsPopup from "./Profile/TermsPopup";
import RulesPopup from "./Profile/RulesPopup";
import KYCPopup from "./Profile/KYCPopup";
import ResponsibleGamePopup from "./Profile/ResponsibleGamePopup";
import AboutPopup from "./Profile/AboutPopup";
import SelfExclusionPopup from "./Profile/SelfExclusionPopup";
import UnderagePolicyPopup from "./Profile/UnderagePolicyPopup";
import URL from "../common/URL";

const LoginSupportSection = ({
  shortPolicy = false,
  supportStyle,
}: {
  shortPolicy?: boolean;
  supportStyle?: string;
}) => {
  const DD = useSelector((e: any) => e.domainDetails);
  const loginSupportStyle = normalizeLoginSupportStyle(
    supportStyle || DD?.loginSupportStyle,
  );
  const whatsappChannel = getContactChannel(DD, "whatsapp");
  const telegramChannel = getContactChannel(DD, "telegram");
  const emailChannel = getContactChannel(DD, "email");
  const skypeChannel = getContactChannel(DD, "skype");
  const instagramChannel = getContactChannel(DD, "instagram");
  const facebookChannel = getContactChannel(DD, "facebook");
  const [privacyPopup, setPrivacyPopup] = useState(false);
  const [termsPopup, setTermsPopup] = useState(false);
  const [rulesPopup, setRulesPopup] = useState(false);
  const [kycPopup, setKycPopup] = useState(false);
  const [responsibleGamePopup, setResponsibleGamePopup] = useState(false);
  const [aboutPopup, setAboutPopup] = useState(false);
  const [selfExclusionPopup, setSelfExclusionPopup] = useState(false);
  const [underagePolicyPopup, setUnderagePolicyPopup] = useState(false);
  const [selectedSkySupportKey, setSelectedSkySupportKey] = useState("");

  const whatsappContacts = whatsappChannel.values;
  const telegramContacts = telegramChannel.values;
  const emailContacts = emailChannel.values;
  const skypeContacts = skypeChannel.values;
  const instagramContacts = instagramChannel.values;
  const showWhatsapp = whatsappChannel.showing;
  const showSupport = isContactShowing(DD, "support");
  const showTelegram = telegramChannel.showing;
  const showEmail = emailChannel.showing;
  const showSkype = skypeChannel.showing;
  const showInstagram = instagramChannel.showing;
  const showFacebook = facebookChannel.showing;
  const facebookValue = facebookChannel.values[0] || "";
  const hasBarSupport =
    showSupport ||
    showWhatsapp ||
    showTelegram ||
    showEmail ||
    showSkype ||
    showInstagram ||
    showFacebook;
  const telegramLabel = telegramHandleLabel(telegramContacts[0]);
  const skySupportItems = [
    {
      key: "email",
      icon: "/icons/supportemail-new.svg",
      alt: "Email",
      enabled: showEmail,
      links: emailContacts.map((contact: string) => ({
        label: contact,
        href: `mailto:${contact}`,
      })),
      href: emailContacts[0] ? `mailto:${emailContacts[0]}` : undefined,
      label: emailContacts[0] || "",
    },
    {
      key: "whatsapp",
      icon: "/icons/whatsapp-new.svg",
      alt: "WhatsApp",
      enabled: showWhatsapp,
      links: whatsappContacts.map((contact: string) => ({
        label: contact,
        href: `${URL.whatsAppSupportUrl}${contact}`,
      })),
      href: whatsappContacts[0]
        ? `${URL.whatsAppSupportUrl}${whatsappContacts[0]}`
        : undefined,
      label: whatsappContacts[0] || "",
    },
    {
      key: "telegram",
      icon: "/icons/telegram-new.svg",
      alt: "Telegram",
      enabled: showTelegram,
      links: telegramContacts.map((contact: string) => ({
        label: telegramHandleLabel(contact) || contact,
        href: `${URL.telegramSupportUrl}${contact}`,
      })),
      href: telegramContacts[0]
        ? `${URL.telegramSupportUrl}${telegramContacts[0]}`
        : undefined,
      label: telegramLabel,
    },
    {
      key: "skype",
      icon: "/images/skype.svg",
      alt: "Skype",
      enabled: showSkype,
      links: skypeContacts.map((contact: string) => ({
        label: contact,
        href: "",
      })),
      href: "",
      label: skypeContacts[0] || "",
    },
    {
      key: "instagram",
      icon: "/images/instagram.svg",
      alt: "Instagram",
      enabled: showInstagram,
      links: instagramContacts.map((contact: string) => ({
        label: contact,
        href: "",
      })),
      href: "",
      label: instagramContacts[0] || "",
    },
    {
      key: "facebook",
      icon: "/icons/facebook-new.svg",
      alt: "Facebook",
      enabled: showFacebook,
      links: facebookChannel.values.map((contact: string) => ({
        label: contact,
        href: /^https?:\/\//i.test(contact)
          ? contact
          : `https://facebook.com/${contact.replace(/^@/, "")}`,
      })),
      href: facebookValue
        ? /^https?:\/\//i.test(facebookValue)
          ? facebookValue
          : `https://facebook.com/${facebookValue.replace(/^@/, "")}`
        : undefined,
      label: facebookValue,
    },
  ];
  const visibleSkySupportItems = skySupportItems.filter((item) => item.enabled);
  const defaultSkySupportIndex =
    visibleSkySupportItems.length >= 2 ? visibleSkySupportItems.length - 2 : 0;
  const defaultSkySupportKey =
    visibleSkySupportItems[defaultSkySupportIndex]?.key || "";
  const activeSkySupportKey =
    selectedSkySupportKey &&
      visibleSkySupportItems.some((item) => item.key === selectedSkySupportKey)
      ? selectedSkySupportKey
      : defaultSkySupportKey;
  const activeSkySupportItem = visibleSkySupportItems.find(
    (item) => item.key === activeSkySupportKey,
  );

  return (
    <>
      {(process.env.REACT_APP_POLICY_LINK === "true" ||
        typeof process.env.REACT_APP_POLICY_LINK === "undefined") && (
          <ul className="policy-link">
            <li>
              <a onClick={() => setPrivacyPopup(true)} style={{ fontSize: "3.46667vw" }}>
                Privacy Policy
              </a>
            </li>
            <li>
              <a onClick={() => setTermsPopup(true)} style={{ fontSize: "3.46667vw" }}>
                Terms and Conditions
              </a>
            </li>
            <li>
              <a onClick={() => setRulesPopup(true)} style={{ fontSize: "3.46667vw" }}>
                Rules and Regulations
              </a>
            </li>
            <li>
              <a onClick={() => setKycPopup(true)} style={{ fontSize: "3.46667vw" }}>KYC</a>
            </li>
            <li>
              <a onClick={() => setResponsibleGamePopup(true)} style={{ fontSize: "3.46667vw" }}>
                Responsible Gaming
              </a>
            </li>
            {!shortPolicy && (
              <>
                <li>
                  <a onClick={() => setAboutPopup(true)} style={{ fontSize: "3.46667vw" }}>About Us</a>
                </li>
                <li>
                  <a onClick={() => setSelfExclusionPopup(true)} style={{ fontSize: "3.46667vw" }}>
                    Self-exclusion Policy
                  </a>
                </li>
                <li>
                  <a onClick={() => setUnderagePolicyPopup(true)} style={{ fontSize: "3.46667vw" }}>
                    Underage Policy
                  </a>
                </li>
              </>
            )}
          </ul>
        )}
      {loginSupportStyle === "skysupport" ? (
        <>
          <style>
            {`
              .login_main.landing-footer-host .login_footer .footer .footer_support--skysupport {
    background: #fff !important;
    // margin-left: calc(50% - 50vw) !important;
    // margin-right: calc(50% - 50vw) !important;
    max-width: 100% !important;
    width: 100% !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    height: auto !important;
}
    @media (max-width: 992px) {
    .home .footer {
        padding: 0 1.6vw;
    }
}          
              `}
          </style>
          <div
            className="footer_support footer_support--skysupport"
            style={{ minHeight: "108px", height: "auto" }}
          >
            <div className="skysupport-icons">
              {visibleSkySupportItems.map((item) => {
                const isActive = item.key === activeSkySupportKey;
                return (
                  <div
                    key={item.key}
                    className={`skysupport-icon-wrapper ${isActive ? "active" : ""}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedSkySupportKey(item.key);
                    }}
                  >
                    <button
                      type="button"
                      className="skysupport-icon-btn"
                      title={item.alt}
                    >
                      <img src={item.icon} alt={item.alt} />
                    </button>
                    {isActive && (
                      <div className="skysupport-notch">
                        <svg
                          width="12"
                          height="7"
                          viewBox="0 0 12 7"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M0 0L6 6L12 0Z" fill="#674a06" />
                          <path
                            d="M0 0L6 6L12 0"
                            fill="none"
                            stroke="#c0b99d"
                            strokeWidth="1"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="skysupport-line" />

            <div
              className="skysupport-handle"
              style={{ minHeight: "47px" }}
            >
              {activeSkySupportItem?.links &&
                activeSkySupportItem.links.length > 0 ? (
                activeSkySupportItem.links.map((linkItem, index) => (
                  <a
                    key={`${activeSkySupportItem.key}-${index}`}
                    href={linkItem.href}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {linkItem.label}
                  </a>
                ))
              ) : activeSkySupportItem && activeSkySupportItem.label ? (
                <a
                  key={activeSkySupportItem.key}
                  href={activeSkySupportItem.href}
                  target="_blank"
                  rel="noreferrer"
                >
                  {activeSkySupportItem.label}
                </a>
              ) : null}
            </div>
          </div>
        </>

      ) : loginSupportStyle === "icon-line" ? (
        <div
          className="footer_support footer_support--icon-line"
          style={{ width: "100%" }}
        >
          <div
            className="support-icon-row"
            style={{ height: "102px" }}
          >
            <span className="support-icon-line" aria-hidden="true" />
            {showTelegram && (
              <div
                className="support-icon-group"
                style={{ position: "relative", top: "-25px" }}
              >
                <a
                  href={
                    telegramContacts[0]
                      ? `${URL.telegramSupportUrl}${telegramContacts[0]}`
                      : undefined
                  }
                  target="_blank"
                  rel="noreferrer"
                  className="support-icon-link"
                  title={telegramContacts[0] || "Telegram"}
                >
                  <img src="/images/telegram.svg" alt="Telegram" />
                  {/* {telegramContacts[0] ? <span>{telegramContacts[0]}</span> : null} */}
                </a>
              </div>
            )}
            {showInstagram && (
              <div
                className="support-icon-group"
                style={{ position: "relative", top: "-25px" }}
              >
                <a
                  href={
                    instagramContacts[0]
                      ? `${URL.instagramSupportUrl}${instagramContacts[0]}`
                      : undefined
                  }
                  target="_blank"
                  rel="noreferrer"
                  className="support-icon-link"
                  title={instagramContacts[0] || "Instagram"}
                >
                  <img src="/images/instagram.svg" alt="Instagram" />
                  {/* {instagramContacts[0] ? <span>{instagramContacts[0]}</span> : null} */}
                </a>
              </div>
            )}
            {showSkype && (
              <div
                className="support-icon-group"
                style={{ position: "relative", top: "-25px" }}
              >
                <a
                  href={
                    skypeContacts[0]
                      ? /^https?:\/\//i.test(skypeContacts[0])
                        ? skypeContacts[0]
                        : `skype:${skypeContacts[0]}?chat`
                      : undefined
                  }
                  target="_blank"
                  rel="noreferrer"
                  className="support-icon-link"
                  title={skypeContacts[0] || "Skype"}
                >
                  <img src="/images/skype.svg" alt="Skype" />
                  {/* {skypeContacts[0] ? (
                      <span>{skypeContacts[0]}</span>
                    ) : null} */}
                </a>
              </div>
            )}
            {showEmail && (
              <div
                className="support-icon-group"
                style={{ position: "relative", top: "-25px" }}
              >
                <a
                  href={
                    emailContacts[0]
                      ? `mailto:${emailContacts[0]}`
                      : undefined
                  }
                  target="_blank"
                  rel="noreferrer"
                  className="support-icon-link"
                  title={emailContacts[0] || "Email"}
                >
                  <img src="/icons/supportemail.svg" alt="Email" />
                  {/* {emailContacts[0] ? (
                      <span>{emailContacts[0]}</span>
                    ) : null} */}
                </a>
              </div>
            )}
            {showFacebook && (
              <div
                className="support-icon-group"
                style={{ position: "relative", top: "-25px" }}
              >
                <a
                  href={
                    facebookValue
                      ? /^https?:\/\//i.test(facebookValue)
                        ? facebookValue
                        : `https://facebook.com/${facebookValue.replace(/^@/, "")}`
                      : undefined
                  }
                  target="_blank"
                  rel="noreferrer"
                  className="support-icon-link"
                  title="Facebook"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="28"
                    height="28"
                    fill="#9e9e9e"
                    aria-label="Facebook"
                  >
                    <path d="M12 0C5.373 0 0 5.373 0 12c0 5.99 4.388 10.954 10.125 11.854V15.47H7.078V12h3.047V9.356c0-3.007 1.792-4.668 4.533-4.668 1.312 0 2.686.234 2.686.234v2.953H15.83c-1.491 0-1.956.925-1.956 1.874V12h3.328l-.532 3.469h-2.796v8.385C19.612 22.954 24 17.99 24 12c0-6.627-5.373-12-12-12z" />
                  </svg>
                </a>
              </div>
            )}
            {showWhatsapp && (
              <div
                className="support-icon-group"
                style={{ position: "relative", top: "-25px" }}
              >
                <a
                  href={
                    whatsappContacts[0]
                      ? `${URL.whatsAppSupportUrl}${whatsappContacts[0]}`
                      : undefined
                  }
                  target="_blank"
                  rel="noreferrer"
                  className="support-icon-link"
                  title="WhatsApp"
                >
                  <img src="/images/whatsapp.svg" alt="WhatsApp" />
                </a>
              </div>
            )}
            <span className="support-icon-line" aria-hidden="true" />
          </div>
        </div>
      ) : hasBarSupport ? (
        <div className="footer_support default-footer-suport-icon">
          {showSupport && (
            <div className="footer_support_item">
              <div className="extend-btn">
                <img
                  src="/images/support.svg"
                  title="customer"
                  className="support-customer"
                />
                <a
                  href={
                    whatsappContacts[0]
                      ? `${URL.whatsAppSupportUrl}${whatsappContacts[0]}`
                      : undefined
                  }
                  target="_blank"
                  style={{ fontSize: "3.4666666667vw", lineHeight: "6.6666666667vw" }}
                >
                  Customer support1
                </a>
                <a
                  href={
                    whatsappContacts[1]
                      ? `${URL.whatsAppSupportUrl}${whatsappContacts[1]}`
                      : undefined
                  }
                  target="_blank"
                  className="split-line"
                  style={{ fontSize: "3.4666666667vw", lineHeight: "6.6666666667vw" }}
                >
                  support2
                </a>
              </div>
            </div>
          )}
          {showWhatsapp && (
            <div className="extend-btn">
              <svg
                className="support-whatsapp"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                aria-label="WhatsApp"
              >
                <path
                  fill="rgba(0,0,0,0.5)"
                  d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"
                />
              </svg>
              <a
                href={
                  whatsappContacts[2]
                    ? `${URL.whatsAppSupportUrl}${whatsappContacts[2]}`
                    : undefined
                }
                target="_blank"
                rel="noreferrer"
              >
                WhatsApp 3
              </a>
              <a
                href={
                  whatsappContacts[3]
                    ? `${URL.whatsAppSupportUrl}${whatsappContacts[3]}`
                    : undefined
                }
                target="_blank"
                rel="noreferrer"
                className="split-line"
              >
                WhatsApp 4
              </a>
            </div>
          )}
          {process.env.REACT_APP_TELEGRAM !== "false" &&
            showTelegram && (
              <div className="extend-btn">
                <a
                  href={
                    telegramContacts[0]
                      ? `${URL.telegramSupportUrl}${telegramContacts[0]}`
                      : undefined
                  }
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontSize: "3.4666666667vw", lineHeight: "6.6666666667vw" }}
                >
                  <img
                    src="/images/telegram.svg"
                    title="Telegram"
                    className="support-Telegram"
                  />
                  Telegram
                </a>
              </div>
            )}
          {showSupport && (
            <div className="footer_support_item support-socials">
              {process.env.REACT_APP_SKYPE !== "false" && showSkype && (
                <div className="extend-btn">
                  <img
                    src="/images/skype.svg"
                    title="Skype"
                    className="support-customer"
                  />
                  <a
                    href={
                      skypeContacts[0]
                        ? `skype:${skypeContacts[0]}?chat`
                        : undefined
                    }
                    target="_blank"
                    style={{ fontSize: "3.4666666667vw", lineHeight: "6.6666666667vw" }}
                  >
                    Skype
                  </a>
                </div>
              )}
              {process.env.REACT_APP_EMAIL !== "false" && showEmail && (
                <div className="extend-btn">
                  <img
                    src="/images/send.svg"
                    title="Email"
                    className="support-mail"
                  />
                  <a
                    href={
                      emailContacts[0]
                        ? `mailto:${emailContacts[0]}`
                        : undefined
                    }
                    target="_blank"
                    style={{ fontSize: "3.4666666667vw", lineHeight: "6.6666666667vw" }}
                  >
                    Email
                  </a>
                </div>
              )}
              {process.env.REACT_APP_INSTAGRAM !== "false" &&
                showInstagram && (
                  <div className="extend-btn">
                    <img
                      src="/images/instagram.svg"
                      title="Instagram"
                      className="support-instagram"
                    />
                    <a
                      href={
                        instagramContacts[0]
                          ? /^https?:\/\//i.test(instagramContacts[0])
                            ? instagramContacts[0]
                            : `${URL.instagramSupportUrl}${instagramContacts[0]}`
                          : undefined
                      }
                      target="_blank"
                      style={{ fontSize: "3.4666666667vw", lineHeight: "6.6666666667vw" }}
                    >
                      Instagram
                    </a>
                  </div>
                )}
              {process.env.REACT_APP_FACEBOOK !== "false" &&
                showFacebook && (
                  <div className="extend-btn">
                    <a
                      href={
                        facebookValue
                          ? /^https?:\/\//i.test(facebookValue)
                            ? facebookValue
                            : `https://facebook.com/${facebookValue.replace(/^@/, "")}`
                          : undefined
                      }
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "3.4666666667vw", lineHeight: "6.6666666667vw"
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="#757575"
                        className="support-Facebook"
                        aria-label="Facebook"
                        style={{
                          width: "4.8666666667vw",
                          height: "4.8666666667vw",
                          marginRight: "5px",
                          flexShrink: 0,
                        }}
                      >
                        <title>Facebook</title>
                        <path d="M12 0C5.373 0 0 5.373 0 12c0 5.99 4.388 10.954 10.125 11.854V15.47H7.078V12h3.047V9.356c0-3.007 1.792-4.668 4.533-4.668 1.312 0 2.686.234 2.686.234v2.953H15.83c-1.491 0-1.956.925-1.956 1.874V12h3.328l-.532 3.469h-2.796v8.385C19.612 22.954 24 17.99 24 12c0-6.627-5.373-12-12-12z" />
                      </svg>
                      Facebook
                    </a>
                  </div>
                )}
            </div>
          )}
        </div>
      ) : null}
      {/* footer poups */}

      {privacyPopup && (
        <CommonPopup
          title={`Rules of Fancy Bets`}
          OpenModal={privacyPopup}
          closeModel={() => setPrivacyPopup(false)}
          customclass="footerpopup"
        >
          <PrivacyPopup />
        </CommonPopup>
      )}

      {termsPopup && (
        <CommonPopup
          title={`Terms & Conditions`}
          OpenModal={termsPopup}
          closeModel={() => setTermsPopup(false)}
          customclass="footerpopup"
        >
          <TermsPopup />
        </CommonPopup>
      )}
      {rulesPopup && (
        <CommonPopup
          title={`Exchange Rules and Regulations`}
          OpenModal={rulesPopup}
          closeModel={() => setRulesPopup(false)}
          customclass="footerpopup"
        >
          <RulesPopup />
        </CommonPopup>
      )}
      {kycPopup && (
        <CommonPopup
          title={`KYC`}
          OpenModal={kycPopup}
          closeModel={() => setKycPopup(false)}
          customclass="footerpopup"
        >
          <KYCPopup />
        </CommonPopup>
      )}
      {responsibleGamePopup && (
        <CommonPopup
          title={`Responsible Gaming`}
          OpenModal={responsibleGamePopup}
          closeModel={() => setResponsibleGamePopup(false)}
          customclass="footerpopup"
        >
          <ResponsibleGamePopup />
        </CommonPopup>
      )}
      {aboutPopup && (
        <CommonPopup
          title={`About Us`}
          OpenModal={aboutPopup}
          closeModel={() => setAboutPopup(false)}
          customclass="footerpopup"
        >
          <AboutPopup />
        </CommonPopup>
      )}
      {selfExclusionPopup && (
        <CommonPopup
          title={`Self-Exclusion Policy`}
          OpenModal={selfExclusionPopup}
          closeModel={() => setSelfExclusionPopup(false)}
          customclass="footerpopup"
        >
          <SelfExclusionPopup />
        </CommonPopup>
      )}
      {underagePolicyPopup && (
        <CommonPopup
          title={`Underage Gaming Policy – ${DD.domain}`}
          OpenModal={underagePolicyPopup}
          closeModel={() => setUnderagePolicyPopup(false)}
          customclass="footerpopup"
        >
          <UnderagePolicyPopup />
        </CommonPopup>
      )}
    </>
  );
};

export default LoginSupportSection;
